/**
 * Journeys API — Phase A W9 backend.
 *
 *   GET    /v1/journeys
 *   POST   /v1/journeys
 *   GET    /v1/journeys/:id
 *   PATCH  /v1/journeys/:id
 *   DELETE /v1/journeys/:id
 *   POST   /v1/journeys/:id/activate | /pause | /archive
 *   GET    /v1/journeys/:id/runs?limit&before&state
 *   GET    /v1/journeys/:id/stats
 *   GET    /v1/journeys/:id/variants
 *   POST   /v1/journeys/:id/runs/:runId/cancel
 *   GET    /v1/journeys/templates-library
 *   POST   /v1/journeys/from-template
 *   POST   /v1/journeys/seed-library
 */
import { apiClient } from "./client";
import type { DeliverabilityResult } from "./analytics";

// ────────── Step + Flow types ──────────

export type StepType =
  | "wait" | "filter" | "send_template"
  | "add_tag" | "remove_tag" | "set_custom_field"
  | "delete_attributes" // IG-33 — удалить атрибуты профиля
  | "webhook" | "split"
  | "grant_loyalty_points" | "show_widget" | "show_experience"
  // SS1 — реактивные блоки (см. cdp-engine domain/journeys/flow.ts)
  | "wait_for_event" | "conditional_split" | "wait_until"
  // Inv-2 Track #2 — closed-loop ноды (NPS-опросы + рефералы + инбокс оператору)
  | "survey_ask" | "referral_grant" | "create_operator_task"
  // P0② — узел подбора предложения (NBO). Зеркало cdp-engine flow.ts.
  | "decide_offer"
  // Эпик «ПОТОКИ» — переменные потока (узел записал → узел прочитал)
  | "set_variable" | "increment_variable"
  // IG-13/14 — мультистрочный «Изменить переменную» (несколько переменных/атрибутов за узел)
  | "change_variables"
  // IG-22 — «Отслеживаемая ссылка» + «Ждать переход по ссылке» (generic SLS)
  | "make_tracked_link" | "wait_link_click"
  // IG-21 — «Сформировать адрес вебхука» + «Ожидание вебхука» (generic SLS)
  | "make_webhook_url" | "wait_webhook"
  // IG-23 — JavaScript-узел (isolated-vm)
  | "js_script"
  // C-Волна2 «сегмент→бот» — запустить сценарий бота для профиля (мостик оркестраторов)
  | "start_bot_scenario";

export type SendPurpose = "promo" | "service" | "transactional";

export interface WaitStep {
  type: "wait";
  duration_seconds: number;
}
export interface FilterStep {
  type: "filter";
  filter: Record<string, unknown>;
  on_fail?: "drop" | "skip";
}
export interface SendTemplateStep {
  type: "send_template";
  template_id: string;
  channel_id?: string | null;
  purpose: SendPurpose;
  vars?: Record<string, unknown>;
  /**
   * B3a — необязательный «получатель через внешнюю функцию».
   *
   * Liquid-выражение, которое рендерится в АДРЕС получателя письма. Обычно
   * содержит тег {% connected_content %}, который дёргает внешнюю функцию
   * (HTTP) и подставляет адрес из ответа. Область видимости рендера включает
   * `event` — payload события-триггера, поэтому шаблон может использовать
   * {{ event.branch_id }} / {{ event.score }} и т.п.
   *
   * Поведение движка (зеркало контракта cdp-engine):
   *   - задано и рендерится в непустое значение с «@» → используется как «to»
   *     вместо profile.primaryEmail;
   *   - пусто / отсутствует / ошибка рендера → откат на profile.primaryEmail.
   *
   * Пусто/undefined ⇒ обычное поведение (письмо на контакт клиента).
   */
  recipient_template?: string;
  /**
   * M3 — preference-тема (preference_topics.id) для проверки топик-согласия. Если
   * задана, письмо/сообщение по этой теме не уйдёт профилю, отписавшемуся от неё.
   * Пусто ⇒ только канальное поведение (как раньше).
   */
  topic_id?: string | null;
}
// IG-30 (мульти-тег): `tag` — legacy одиночный, `tags` — новый массив (несколько за узел).
export interface AddTagStep { type: "add_tag"; tag?: string; tags?: string[]; }
export interface RemoveTagStep { type: "remove_tag"; tag?: string; tags?: string[]; }
export interface SetCustomFieldStep {
  type: "set_custom_field";
  key: string;
  value: string | number | boolean | null;
}
/** IG-33 — удалить перечисленные атрибуты профиля. */
export interface DeleteAttributesStep {
  type: "delete_attributes";
  keys: string[];
}
export interface WebhookStep {
  type: "webhook";
  url: string;
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  headers?: Record<string, string>;
  body_template?: string;
  /** IG-42c — тип тела: raw/json (из body_template) | form/multipart (из body_form). */
  body_type?: "raw" | "json" | "form" | "multipart";
  /** IG-42c — пары поле-значение для form/multipart (значения шаблонизируемы Liquid). */
  body_form?: Record<string, string>;
  fail_on_error?: boolean;
  /** IG-42 — время ожидания ответа, сек (1..120); пусто → стандартное. */
  timeout_seconds?: number;
  /**
   * IG-42d — опц. ветвление по исходу вызова (эталон = бот `api_call`). `true` → узел
   * даёт под-потоки успех/ошибка/таймаут (HTTP выполняется один раз, исход выбирает ветку).
   * При флаге `fail_on_error` игнорируется. Отсутствует/`false` → узел линейный (совместимость).
   */
  branch_on_outcome?: boolean;
  /** Ветка «успех» (2xx). */
  on_success?: FlowStep[];
  /** Ветка «ошибка» (non-2xx / сеть / SSRF-блок). */
  on_error?: FlowStep[];
  /** Ветка «таймаут» (AbortError). Отсутствует → таймаут идёт по ветке «ошибка». */
  on_timeout?: FlowStep[];
}
export interface EmitEventStep {
  type: "emit_event";
  event_type: string;
  semantic_type?: string;
  payload?: Record<string, unknown>;
}
export interface GrantLoyaltyPointsStep {
  type: "grant_loyalty_points";
  /** Liquid template that renders an integer, or a number. */
  amount: number | string;
  /** Free-text reason stored on the ledger entry (e.g. "welcome_d0"). */
  reason_code: string;
}
export interface ShowWidgetStep {
  type: "show_widget";
  /** Slug from widget_definitions. */
  widget_slug: string;
  /** Override surface; defaults to widget's first declared surface. */
  surface?: "web" | "telegram_bot" | "max_bot";
  /** Pending TTL in seconds. Default 7 days. */
  ttl_seconds?: number;
  /** Override widget content (merged at render time). */
  content_overrides?: Record<string, unknown>;
}
/**
 * Inv-1 / Experiences Wave B (γ) — показать впечатление по событию журнея.
 * Зеркало cdp-engine flow.ts: `experience_id` обязателен (validateFlow это
 * проверяет). Направленный показ живёт до показа (TTL пока нет).
 */
export interface ShowExperienceStep {
  type: "show_experience";
  /** id записи experiences (обязателен). */
  experience_id: string;
  /** Поверхность доставки; по умолчанию 'web'. */
  surface?: "web" | "telegram_bot" | "max_bot";
}
export interface SplitVariant {
  id: string;
  label?: string;
  weight?: number;
  flow: FlowStep[];
}
export interface SplitStep {
  type: "split";
  mode: "random" | "weighted";
  variants: SplitVariant[];
}

// ── SS1 «Реактивные журнеи» — реактивные блоки ──
// Зеркало cdp-engine domain/journeys/flow.ts. Под-флоу веток исполняются
// инлайн той же машинерией, что у split. Внутри ветки эти блоки вкладывать
// нельзя (движок отвергает) — редактор это запрещает.

/** Один предикат по payload события (реюз из сегмент-фильтров). */
export interface EventPayloadPredicate {
  attr: string;
  op: "eq" | "neq" | "in" | "not_in" | "gt" | "gte" | "lt" | "lte" | "contains" | "starts_with";
  value: unknown;
}

/** Ждать событие профиля до таймаута, затем разветвиться. */
export interface WaitForEventStep {
  type: "wait_for_event";
  event_type: string;
  /** Целые секунды, > 0, ≤ 90 дней. */
  timeout_seconds: number;
  /** Опц. предикаты по payload события (AND). Резолвит только подходящее событие. */
  match?: EventPayloadPredicate[];
  /**
   * IG-44 — дополнительный источник пробуждения «дата»:
   *   • 'duration' (умолч.) — дедлайн = now + timeout_seconds;
   *   • 'date_expr'         — дедлайн = дата из профиля ± смещение (`deadline_expr`,
   *     формат «<база> ± N(s|m|h|d|w)»). Не резолвится → фолбэк на timeout_seconds.
   */
  deadline_mode?: "duration" | "date_expr";
  deadline_expr?: string;
  /** Ветка «событие пришло». */
  on_event: FlowStep[];
  /** Ветка «истёк таймаут». */
  on_timeout: FlowStep[];
}

// ── IG-22 — «Ждать переход по ссылке» (ветвящийся: клик / таймаут; зеркало cdp-engine) ──
/**
 * Ожидатель клика по трекинг-ссылке (make_tracked_link на ту же переменную). Специализация
 * wait_for_event: ждёт `link.clicked` с авто-match по `link_id = ${runId}:${link_var}`.
 */
export interface WaitLinkClickStep {
  type: "wait_link_click";
  /** Имя переменной со ссылкой (та же, что у make_tracked_link). Умолч. `tracked_link`. */
  link_var?: string;
  /** Целые секунды, > 0, ≤ 90 дней (также фолбэк-дедлайн для date_expr). */
  timeout_seconds: number;
  deadline_mode?: "duration" | "date_expr";
  deadline_expr?: string;
  /** Ветка «клик пришёл». */
  on_click: FlowStep[];
  /** Ветка «истёк таймаут». */
  on_timeout: FlowStep[];
}

// ── IG-21 — «Ожидание вебхука» (ветвящийся: вебхук / таймаут; зеркало cdp-engine) ──
/**
 * Генерит per-run callback-URL (кладёт в `hook_var`, умолч. `webhook_url`), ждёт, пока
 * внешняя система дёрнет URL. Специализация wait_for_event по событию `webhook.received`.
 */
export interface WaitWebhookStep {
  type: "wait_webhook";
  /** Имя переменной для callback-URL. Умолч. `webhook_url`. */
  hook_var?: string;
  timeout_seconds: number;
  deadline_mode?: "duration" | "date_expr";
  deadline_expr?: string;
  /** Ветка «вебхук получен». */
  on_received: FlowStep[];
  /** Ветка «истёк таймаут». */
  on_timeout: FlowStep[];
}

/** Одна ветка условного ветвления: фильтр + под-флоу. */
export interface ConditionalBranch {
  id: string;
  label?: string;
  filter: Record<string, unknown>;
  flow: FlowStep[];
}

/** Детерминированное if/elif/else по сегмент-фильтрам. */
export interface ConditionalSplitStep {
  type: "conditional_split";
  branches: ConditionalBranch[];
  default_flow: FlowStep[];
}

/** Календарный таймер — разбудить прогон в ближайший момент в ЧП профиля. */
export interface WaitUntilStep {
  type: "wait_until";
  /** IG-20 — `date_expr`: value = `{{ дата }} ± N(s|m|h|d|w)`, дата из атрибута профиля. */
  mode: "time_of_day" | "day_of_month" | "date" | "day_of_week" | "date_expr";
  value: string;
  /** 'profile' → profile.timezone; иначе явный IANA-пояс. */
  timezone?: "profile" | string;
}

// ── Inv-2 Track #2 «Closed-loop журнеи» — три ЛИСТОВЫХ ноды (зеркало cdp-engine flow.ts) ──
export interface SurveyAskStep {
  type: "survey_ask";
  survey_id: string;
  channel_id: string;
  template_id: string;
  purpose: "service" | "transactional";
  /** M3 — preference-тема для топик-согласия (см. SendTemplateStep.topic_id). */
  topic_id?: string | null;
}
export interface ReferralGrantStep {
  type: "referral_grant";
  program_id: string;
}
/**
 * P0② — «Подобрать предложение» (NBO). Листовой шаг: движок ранжирует все
 * активные предложения тенанта под профиль и записывает выбор в атрибут.
 * Контракт намеренно минимален — зеркало cdp-engine domain/journeys/flow.ts:
 * выбор офферов/eligibility/экономика живут в сущности «предложения», а не в
 * шаге. РОВНО два опциональных поля.
 *
 * Результат пишется в profile.attributes[write_attribute] = offer.id и
 * [write_attribute + "_name"] = offer.name (по умолчанию nbo_offer /
 * nbo_offer_name). Дальше читается в шаблонах через
 * {{ profile.attributes.nbo_offer_name }}.
 */
export interface DecideOfferStep {
  type: "decide_offer";
  /** Атрибут профиля под выбранное предложение. По умолчанию nbo_offer. */
  write_attribute?: string;
  /** Поверхность подбора — только контекст/телеметрия. */
  surface?: "web" | "telegram_bot" | "max_bot";
}
export interface CreateOperatorTaskStep {
  type: "create_operator_task";
  title: string;
  kind?: string;
  priority?: "low" | "normal" | "high";
  survey_id?: string;
  include_recovery_script?: boolean;
  description_template?: string;
}

// ── Эпик «ПОТОКИ» — переменные потока (зеркало cdp-engine flow.ts) ──
/** Записать переменную потока. value — строка (может быть Liquid), число, флаг или JSON. */
export interface SetVariableStep {
  type: "set_variable";
  key: string;
  value: unknown;
}
/** Числовой инкремент переменной потока. delta по умолчанию 1. */
export interface IncrementVariableStep {
  type: "increment_variable";
  key: string;
  delta?: number;
}

// ── IG-13/14 — мультистрочный «Изменить переменную» (зеркало cdp-engine variable-ops.ts) ──
export type VariableOpTarget = "variable" | "attribute";
export type VariableOpKind = "set" | "increment" | "decrement" | "append";
/** Одна строка операции: цель + ключ + операция + значение. */
export interface VariableOp {
  /** «variable» — переменная потока (vars.*); «attribute» — атрибут профиля. По умолч. variable. */
  target?: VariableOpTarget;
  key: string;
  /** «set» задать | «increment» увеличить | «decrement» уменьшить | «append» добавить в список. */
  op?: VariableOpKind;
  value?: unknown;
}
/** Один узел меняет НЕСКОЛЬКО переменных/атрибутов за раз. */
export interface ChangeVariablesStep {
  type: "change_variables";
  ops: VariableOp[];
}

// ── IG-22 — «Отслеживаемая ссылка» (generic SLS, зеркало cdp-engine flow.ts) ──
/**
 * Формирует подписанную короткую трекинг-ссылку на `target_url` и кладёт готовый URL
 * в переменную потока `link_var` (по умолч. `tracked_link`). Instant-узел. Клик по
 * ссылке пишет событие `link.clicked` и редиректит на цель; узел-ожидатель клика
 * (`wait_link_click`, IG-22) ловит клик по той же переменной.
 */
export interface MakeTrackedLinkStep {
  type: "make_tracked_link";
  /** Целевой URL (Liquid) — куда редиректить после клика. */
  target_url: string;
  /** Имя переменной потока для готовой ссылки. По умолч. `tracked_link`. */
  link_var?: string;
}

// ── IG-21 — «Сформировать адрес вебхука» (instant, зеркало cdp-engine) ──
/** Чеканит per-run callback-URL и кладёт в `hook_var` (умолч. `webhook_url`) для пары с wait_webhook. */
export interface MakeWebhookUrlStep {
  type: "make_webhook_url";
  /** Имя переменной потока для callback-URL. По умолч. `webhook_url`. */
  hook_var?: string;
}

// ── IG-23 — JS-узел (isolated-vm песочница, зеркало cdp-engine) ──
/** Исполняет пользовательский JS в песочнице; `input`={profile,vars,event}; return→result_var. */
export interface JsScriptStep {
  type: "js_script";
  code: string;
  /** Переменная для результата. Умолч. `js_result`. */
  result_var?: string;
  /** Таймаут выполнения, мс (1..5000). */
  timeout_ms?: number;
}

// ── C-Волна2 «сегмент→бот» — запустить сценарий бота для профиля (зеркало cdp-engine) ──
/**
 * Проактивно стартует диалог бота у профиля прогона. Честное ограничение (правило
 * мессенджеров): бот пишет первым ТОЛЬКО подписчику (есть чат с ботом) — иначе узел
 * «пропущен». Настраивается: бот + его сценарий.
 */
export interface StartBotScenarioStep {
  type: "start_bot_scenario";
  bot_id: string;
  flow_id: string;
}

// ── Волна 3 «Умный CDP» — ИИ-узлы В СЦЕНАРИЯХ (зеркало cdp-engine domain/journeys/flow.ts) ──
// 5 листовых узлов: LLM на лету «текст → структура/текст», результат в переменную потока
// (`output_var`). Ветвление — существующим «Ветвление по условию» (conditional_split) по vars.<output_var>.

/** Общее: `input` — Liquid-шаблон источника текста ({{ event.* }}/{{ vars.* }}/{{ profile.* }}). */
interface AiJourneyStepBase {
  input?: string;
  output_var?: string;
  /** Опц. атрибут профиля, куда ДОПОЛНИТЕЛЬНО записать результат («память агента»). */
  write_attribute?: string;
}
/** Сгенерировать текст по инструкции (тема письма, SMS-копирайт). */
export interface AiGenerateStep extends AiJourneyStepBase {
  type: "ai_generate";
  instruction: string;
  max_length?: number;
  tone?: string;
  forbidden_claims?: string[];
}
/** Отнести вход к одной из категорий → ветвим «по условию» по vars.<output_var>. */
export interface AiClassifyStep extends AiJourneyStepBase {
  type: "ai_classify";
  categories: { key: string; description?: string }[];
}
/** Извлечь поля из свободного текста в объект-переменную. */
export interface AiExtractStep extends AiJourneyStepBase {
  type: "ai_extract";
  fields: { key: string; description?: string }[];
}
/** Принять решение из вариантов (+ причина в `<output_var>_reason`). */
export interface AiDecideStep extends AiJourneyStepBase {
  type: "ai_decide";
  question: string;
  choices: { key: string; description?: string }[];
}
/** Краткое резюме длинного текста. */
export interface AiSummarizeStep extends AiJourneyStepBase {
  type: "ai_summarize";
  max_length?: number;
}

/**
 * Ф3.5 — «Действие интеграции»: выполнить действие внешней системы (Jira и т.п.) через подключение
 * из реестра интеграций. Исход (ok/not_found/error) → `vars.<output_var>` (+ `<output_var>_entity_id`),
 * далее ветвим «по условию». Каталог провайдеров/действий/полей — `GET /v1/integrations/catalog`.
 */
/** Правило field_map узла «Действие интеграции»: имя поля внешней системы ← Liquid-значение. */
export interface ConnectorFieldMapRule { field: string; source: { type: "liquid"; value: string }; }

export interface ConnectorActionStep {
  type: "connector_action";
  /** Провайдер (совпадает с provider подключения): jira | … */
  provider: string;
  /** Ключ действия каталога: assign_issue | transition_issue | … */
  action: string;
  /** id подключения в реестре интеграций. */
  connection_id: string;
  /** Значения полей действия: текст/селектор → строка (Liquid); field_map → массив правил «поле ← значение». */
  config?: Record<string, string | ConnectorFieldMapRule[]>;
  /** Переменная потока для ветки исхода (умолч. connector_result). */
  output_var?: string;
}

/** Координата узла на холсте редактора Сценариев (свободная раскладка). */
export interface FlowStepPos {
  x: number;
  y: number;
}

/**
 * Метаданные шага, значимые только для ВИЗУАЛЬНОГО редактора (холст). Движок их
 * не интерпретирует, а journey-репозиторий хранит `flow` как JSONB «как есть»
 * (`validateFlow` лишние поля шага не отвергает — проверено), поэтому позиция,
 * раз положенная на шаг, переживает save → reload. Имя с подчёркиванием —
 * чтобы визуально отделить «служебное для UI» от доменных полей шага.
 */
export interface FlowStepCanvasMeta {
  /**
   * Свободная позиция узла на холсте. Если задана — холст ставит узел сюда
   * (перетащенный/закреплённый); если нет — раскладывает автоматически (dagre).
   */
  _pos?: FlowStepPos;
  /**
   * IG-32 — опциональное «название узла» (как у бота/кампании): человекочитаемая
   * подпись поверх типового заголовка (напр. «Напоминание за 3 дня»). Движок его
   * не интерпретирует; journey-репозиторий хранит `flow` как JSONB «как есть».
   */
  name?: string;
}

/**
 * Фаза 4 — «Отправить в мессенджер»: доставить богатую карточку в канал
 * «Мессенджер» текущему профилю (зеркало cdp-engine SendMessengerCardStep).
 * target='inbox' → персональный тред «Уведомления»; target='feed' → лента.
 */
export interface SendMessengerCardStep {
  type: "send_messenger_card";
  target: "inbox" | "feed";
  feed_template_id?: string | null;
  content_type?: "hero" | "video" | "text";
  title: string;
  body?: string | null;
  media?: { url: string; kind: "image" | "video" } | null;
  cta?: { style: "links" | "button"; items: Array<{ text: string; action: { type: string; target: string } }> } | null;
  delivery_mode?: "inbox_only" | "push_and_inbox" | "silent_preload" | "in_app_only";
}

export type FlowStep = (
  | WaitStep | FilterStep | SendTemplateStep
  | AddTagStep | RemoveTagStep | SetCustomFieldStep | DeleteAttributesStep
  | WebhookStep | EmitEventStep | SplitStep
  | GrantLoyaltyPointsStep | ShowWidgetStep | ShowExperienceStep
  | WaitForEventStep | ConditionalSplitStep | WaitUntilStep
  | SurveyAskStep | ReferralGrantStep | CreateOperatorTaskStep
  | DecideOfferStep
  | SetVariableStep | IncrementVariableStep | ChangeVariablesStep
  | MakeTrackedLinkStep | MakeWebhookUrlStep | WaitLinkClickStep | WaitWebhookStep
  | JsScriptStep | StartBotScenarioStep
  | AiGenerateStep | AiClassifyStep | AiExtractStep | AiDecideStep | AiSummarizeStep
  | ConnectorActionStep | SendMessengerCardStep
) & FlowStepCanvasMeta;

// ── Ф3.5 — каталог интеграций для пикера узла connector_action ──
export interface IntegrationCatalogFieldOption { value: string; label: string; }
export interface IntegrationCatalogField {
  key: string; title: string; required?: boolean; hint?: string;
  /** Контрол поля: text (умолч.) | field_map (динамические пары «поле ← значение»). */
  kind?: "text" | "field_map";
  /** Если задано — поле выбирается из списка (селектор). */
  options?: IntegrationCatalogFieldOption[];
  /** Поле доступно ТОЛЬКО на бот-холсте (транскрипт/ИИ-обогащение из диалога) — на журнеях/автоматизациях скрыто. */
  botOnly?: boolean;
}
export interface IntegrationCatalogAction { provider: string; action: string; title: string; configFields: IntegrationCatalogField[]; branches: string[]; }
export interface IntegrationCatalogProvider { provider: string; title: string; authType: string; actions: IntegrationCatalogAction[]; }
export interface IntegrationConnectionRow { id: string; name: string; provider: string; enabled: boolean; }

/** Каталог провайдеров/действий интеграций (для пикера в конструкторе сценариев). */
export async function fetchIntegrationCatalog(): Promise<IntegrationCatalogProvider[]> {
  const { data } = await apiClient.get<{ providers: IntegrationCatalogProvider[] }>("/v1/integrations/catalog");
  return data.providers ?? [];
}

/** Подключения интеграций аренды (для выбора connection_id; секреты не отдаются). */
export async function fetchIntegrationConnections(): Promise<IntegrationConnectionRow[]> {
  const { data } = await apiClient.get<{ connections: IntegrationConnectionRow[] }>("/v1/integrations/connections");
  return data.connections ?? [];
}

// ── Эпик «ПОТОКИ» — определения переменных потока («схема» сценария) ──
export type FlowVariableType = "string" | "number" | "boolean" | "json";
export type FlowVariableScope = "run" | "journey";
export interface FlowVariableDef {
  key: string;
  type: FlowVariableType;
  scope: FlowVariableScope;
  default: unknown;
  description?: string;
}

// ────────── Journey types ──────────

export type JourneyStatus = "draft" | "active" | "paused" | "archived";
export type ReentryPolicy = "once" | "always" | "once_per_24h" | "once_per_7d" | "once_per_30d";
export type JourneyRunState =
  | "enrolled" | "waiting" | "running" | "completed"
  | "cancelled" | "goal_hit" | "failed" | "holdout";

export interface FlowStats {
  topLevelSteps: number;
  totalSteps: number;
  waitSteps: number;
  sendSteps: number;
  splitSteps: number;
  totalDurationSeconds: number;
}

export interface Journey {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  entry_event_type: string;
  entry_filter: Record<string, unknown> | null;
  reentry_policy: ReentryPolicy;
  cancel_event_types: string[];
  goal_event_type: string | null;
  holdout_percent: number;
  priority: number;
  flow: FlowStep[];
  flow_stats: FlowStats;
  /** Эпик «ПОТОКИ» — определения переменных потока сценария. */
  flow_variables: FlowVariableDef[];
  status: JourneyStatus;
  source_template_key: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  activated_at: string | null;
  archived_at: string | null;
}

export interface JourneyListResponse {
  rows: Journey[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

/**
 * IG-45 — «Сетевой инспектор» HTTP-узла в трассе прогона (сценарий `webhook` / бот `api_call`).
 * Зеркало движка `domain/integrations/http-network-trace.ts`. Секретные заголовки уже
 * замаскированы «***» на бэкенде, тела обрезаны.
 */
export interface HttpNetworkTrace {
  method: string;
  url: string;
  req_headers?: Record<string, string>;
  req_body?: string;
  status?: number | null;
  resp_body?: string;
  timeout?: boolean;
  error?: string;
}

export interface JourneyRun {
  id: string;
  journey_id: string;
  tenant_id: string;
  profile_id: string;
  state: JourneyRunState;
  current_step_index: number;
  next_action_at: string | null;
  enrolled_at: string;
  completed_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  variant_assignments: Record<string, string>;
  step_log: Array<{
    step_index: number;
    kind: StepType;
    action: string;
    at: string;
    variant_id?: string;
    reason?: string;
    error?: string;
    meta?: Record<string, unknown> & { net?: HttpNetworkTrace };
  }>;
  enrolled_by_event_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface JourneyRunsResponse {
  runs: JourneyRun[];
  next_before: string | null;
}

export interface JourneyStats {
  total: number;
  byState: Record<JourneyRunState, number>;
  goalHitRate: number;
}

export interface JourneyVariantReport {
  variantId: string;
  variantLabel: string | null;
  stepIndex: number;
  count: number;
  completed: number;
  goalHit: number;
  completionRate: number;
  goalHitRate: number;
}

/**
 * Ф2 — текущие агрегаты по шагам сценария для live-метрик на холсте.
 *
 * Контракт ПОЗИЦИОННЫЙ (без стабильных node-id): шаг адресуется индексом в
 * `flow` верхнего уровня (`stepIndex`), а ветка форка — парой (kind, id),
 * которую фронт переводит в canvas-id через `forkKeyOf`. Бэкенд ОБЯЗАН вернуть
 * `kind` ровно из набора ниже — сырую stored-строку variant_id фронт не видит.
 */
export type JourneyForkKind = "variant" | "branch" | "default" | "on_event" | "on_timeout";

/** Метрика top-level шага: сколько профилей его достигли и сколько сейчас ждут. */
export interface JourneyNodeStat {
  /** Индекс шага в `flow` верхнего уровня. */
  stepIndex: number;
  /** Число различных профилей, исполнивших/достигших шаг. */
  executed: number;
  /** Текущее число pending в ожидании события (0 для не-wait узлов). */
  waiting: number;
}

/** Метрика ветки форка: сколько профилей выбрали именно эту ветвь. */
export interface JourneyBranchStat {
  stepIndex: number;
  kind: JourneyForkKind;
  /** id варианта (split) или ветки (conditional_split); для default/on_event/on_timeout отсутствует. */
  id?: string;
  count: number;
}

export interface JourneyNodeStats {
  nodes: JourneyNodeStat[];
  branches: JourneyBranchStat[];
}

// ────────── Library types ──────────

export interface LibraryTemplate {
  key: string;
  name: string;
  description: string;
  suggested_journey_name: string;
  entry_event_type: string;
  cancel_event_types: string[];
  goal_event_type: string | null;
  reentry_policy: ReentryPolicy;
  priority: number;
  holdout_percent: number;
  default_flow: FlowStep[];
  flow_stats: FlowStats;
  required_event_types: string[];
  required_template_names: string[];
}

export interface CreateJourneyInput {
  name: string;
  description?: string | null;
  entry_event_type: string;
  entry_filter?: Record<string, unknown> | null;
  reentry_policy?: ReentryPolicy;
  cancel_event_types?: string[];
  goal_event_type?: string | null;
  holdout_percent?: number;
  priority?: number;
  flow: FlowStep[];
}

export type UpdateJourneyInput = Partial<CreateJourneyInput>;

export interface FromTemplateInput {
  key: string;
  customizations?: {
    name?: string;
    entry_filter?: Record<string, unknown> | null;
    holdout_percent?: number;
    priority?: number;
    send_template_overrides?: Record<string, string>;
  };
}

export interface WinbackTrigger {
  id: string;
  segment_id: string;
  event_type: string;
  emit_on: "enter" | "exit" | "both";
  schedule: string;
  enabled: boolean;
}

// ────────── API ──────────

// ────────── Simulator (Ф0/Ф1) — пробный прогон одного профиля ──────────

/** Редактируемое мок-состояние тест-профиля (редактор состояния, Ф2). */
export interface SimProfileState {
  attributes?: Record<string, unknown>;
  tags?: string[];
  primary_email?: string | null;
  primary_phone?: string | null;
  display_name?: string | null;
  lifecycle_stage?: string | null;
  is_global_holdout?: boolean;
  first_seen_at?: string | null;
  last_seen_at?: string | null;
  timezone?: string | null;
  loyalty?: {
    balance?: number | null;
    lifetime_earned?: number | null;
    lifetime_spent?: number | null;
    tier?: string | null;
    wallets?: Record<string, unknown> | null;
  } | null;
  /** Значения полей, требующих БД (events / segment / journey membership и т.п.). Ключ = путь поля. */
  facts?: Record<string, unknown>;
}

export interface SimWaitForEventOutcome {
  resolution: "event" | "timeout";
  payload?: Record<string, unknown>;
  after_seconds?: number;
}

export type SimAction =
  | "executed" | "sent" | "branch" | "wait"
  | "filter_pass" | "dropped" | "skipped" | "external";

export interface SimOutgoing {
  topIndex: number;
  channel_id?: string | null;
  template_id: string;
  purpose: string;
  is_survey: boolean;
  note: string;
}

export interface SimSkipped {
  topIndex: number;
  stepType: string;
  note: string;
}

export interface SimStateSnapshot {
  attributes: Record<string, unknown>;
  tags: string[];
  lifecycle_stage?: string | null;
  loyalty?: { balance?: number | null; tier?: string | null; lifetime_earned?: number | null } | null;
  /** Эпик «ПОТОКИ» Ф3 — значения переменных потока на этом шаге (vars.*). */
  variables?: Record<string, unknown>;
}

export interface SimStep {
  seq: number;
  /** Индекс top-level узла — холст подсвечивает t{topIndex}. */
  topIndex: number;
  depth: number;
  branchPath: string[];
  stepType: string;
  title: string;
  action: SimAction;
  detail?: string;
  virtualTime: string;
  stateAfter: SimStateSnapshot;
  outgoing?: SimOutgoing;
  skipped?: SimSkipped;
  waitSeconds?: number;
}

export interface JourneySimulateResult {
  status: "completed" | "cancelled" | "failed" | "waiting";
  reason: string;
  steps: SimStep[];
  outgoing: SimOutgoing[];
  skipped: SimSkipped[];
  finalState: SimStateSnapshot;
  virtualStart: string;
  virtualEnd: string;
  stats: { topLevelSteps: number; totalSteps: number; waitSteps: number; sendSteps: number; splitSteps: number; totalDurationSeconds: number };
  unknowns: string[];
}

export interface JourneySimulateInput {
  /** Черновик графа с холста; если не задан — сервер берёт сохранённый флоу. */
  flow?: FlowStep[];
  profile?: SimProfileState;
  event_payload?: Record<string, unknown>;
  now?: string;
  /** Исход wait_for_event по индексу top-level узла (ключ — строка индекса). */
  wait_for_event_outcomes?: Record<string, SimWaitForEventOutcome>;
  split_seed?: string;
}

/** Снимок версии потока сценария (Эпик «ПОТОКИ» §5) — облегчённый (список). */
export interface JourneyFlowVersion {
  id: number;
  versionNumber: number;
  name: string;
  stepCount: number;
  variableCount: number;
  comment: string | null;
  authorId: string | null;
  source: string;
  sha256: string;
  createdAt: string;
}

interface JourneyFlowVersionWire {
  id: number;
  version_number: number;
  name: string;
  step_count: number;
  variable_count: number;
  comment: string | null;
  author_id: string | null;
  source: string;
  sha256: string;
  created_at: string;
}

function journeyVersionFromWire(w: JourneyFlowVersionWire): JourneyFlowVersion {
  return {
    id: w.id,
    versionNumber: w.version_number,
    name: w.name,
    stepCount: w.step_count,
    variableCount: w.variable_count,
    comment: w.comment ?? null,
    authorId: w.author_id ?? null,
    source: w.source,
    sha256: w.sha256,
    createdAt: w.created_at,
  };
}

export const journeysApi = {
  async list(params: { limit?: number; offset?: number; status?: JourneyStatus | "all" } = {}): Promise<JourneyListResponse> {
    const res = await apiClient.get<JourneyListResponse>("/v1/journeys", { params });
    return res.data;
  },
  async get(id: string): Promise<Journey> {
    const res = await apiClient.get<{ journey: Journey }>(`/v1/journeys/${id}`);
    return res.data.journey;
  },
  /** Пробный прогон (симулятор-отладчик) — read-only, ничего не пишет/шлёт. */
  async simulate(id: string, input: JourneySimulateInput): Promise<JourneySimulateResult> {
    const res = await apiClient.post<{ simulation: JourneySimulateResult }>(`/v1/journeys/${id}/simulate`, input);
    return res.data.simulation;
  },
  /** Эпик «ПОТОКИ» — определения переменных потока сценария (весь набор). */
  async getFlowVariables(id: string): Promise<FlowVariableDef[]> {
    const res = await apiClient.get<{ variables: FlowVariableDef[] }>(`/v1/journeys/${id}/flow-variables`);
    return res.data.variables;
  },
  /** Заменить набор определений переменных потока целиком. Возвращает нормализованный набор. */
  async setFlowVariables(id: string, variables: FlowVariableDef[]): Promise<FlowVariableDef[]> {
    const res = await apiClient.put<{ variables: FlowVariableDef[] }>(`/v1/journeys/${id}/flow-variables`, { variables });
    return res.data.variables;
  },
  /** Эпик «ПОТОКИ» §5 — история версий потока (снимок при активации, свежие сверху). */
  async getVersions(id: string): Promise<JourneyFlowVersion[]> {
    const res = await apiClient.get<{ versions: JourneyFlowVersionWire[] }>(`/v1/journeys/${id}/versions`);
    return (res.data.versions ?? []).map(journeyVersionFromWire);
  },
  /** Откат к версии: грузит снимок обратно и сбрасывает статус в черновик. */
  async restoreVersion(id: string, versionNumber: number): Promise<Journey> {
    const res = await apiClient.post<{ journey: Journey }>(`/v1/journeys/${id}/versions/${versionNumber}/restore`, {});
    return res.data.journey;
  },
  async create(input: CreateJourneyInput): Promise<Journey> {
    const res = await apiClient.post<{ journey: Journey }>("/v1/journeys", input);
    return res.data.journey;
  },
  /** IG-34 — дублировать сценарий (копия-черновик, имя «(копия)»). */
  async duplicate(id: string, body?: { name?: string }): Promise<Journey> {
    const res = await apiClient.post<{ journey: Journey }>(`/v1/journeys/${id}/duplicate`, body ?? {});
    return res.data.journey;
  },
  async update(id: string, input: UpdateJourneyInput): Promise<Journey> {
    const res = await apiClient.patch<{ journey: Journey }>(`/v1/journeys/${id}`, input);
    return res.data.journey;
  },
  async deleteJourney(id: string): Promise<void> {
    await apiClient.delete(`/v1/journeys/${id}`);
  },
  async activate(id: string): Promise<Journey> {
    const res = await apiClient.post<{ journey: Journey }>(`/v1/journeys/${id}/activate`, {});
    return res.data.journey;
  },
  async pause(id: string): Promise<Journey> {
    const res = await apiClient.post<{ journey: Journey }>(`/v1/journeys/${id}/pause`, {});
    return res.data.journey;
  },
  async archive(id: string): Promise<Journey> {
    const res = await apiClient.post<{ journey: Journey }>(`/v1/journeys/${id}/archive`, {});
    return res.data.journey;
  },
  /**
   * Wave 4.9 — DELETE polymorphic: hard-delete for draft, archive otherwise.
   * Response shape: `{ deleted: true, mode: 'hard' } | { archived: true, mode: 'soft' }`.
   */
  async remove(id: string): Promise<{ mode: "hard" | "soft" }> {
    const res = await apiClient.delete<{ deleted?: boolean; archived?: boolean; mode: "hard" | "soft" }>(
      `/v1/journeys/${id}`,
    );
    return { mode: res.data.mode };
  },
  async runs(id: string, opts: { limit?: number; before?: string | null; state?: JourneyRunState | "all" } = {}): Promise<JourneyRunsResponse> {
    const params: Record<string, string | number> = {};
    if (opts.limit !== undefined) params.limit = opts.limit;
    if (opts.before) params.before = opts.before;
    if (opts.state) params.state = opts.state;
    const res = await apiClient.get<JourneyRunsResponse>(`/v1/journeys/${id}/runs`, { params });
    return res.data;
  },
  async cancelRun(journeyId: string, runId: string, reason?: string): Promise<JourneyRun> {
    const res = await apiClient.post<{ run: JourneyRun }>(
      `/v1/journeys/${journeyId}/runs/${runId}/cancel`, { reason },
    );
    return res.data.run;
  },
  async stats(id: string): Promise<JourneyStats> {
    const res = await apiClient.get<{ stats: JourneyStats }>(`/v1/journeys/${id}/stats`);
    return res.data.stats;
  },
  async variants(id: string): Promise<JourneyVariantReport[]> {
    const res = await apiClient.get<{ variants: JourneyVariantReport[] }>(`/v1/journeys/${id}/variants`);
    return res.data.variants;
  },
  /**
   * Ф2 — текущие агрегаты по шагам для live-метрик на холсте редактора сценария.
   * Возвращает населённость узлов (исполнено / ждут) и распределение по веткам
   * форков. Толерантен к snake_case/camelCase в теле ответа.
   */
  async nodeStats(id: string): Promise<JourneyNodeStats> {
    const res = await apiClient.get<{
      nodes?: Array<{ step_index?: number; stepIndex?: number; executed?: number; waiting?: number }>;
      branches?: Array<{ step_index?: number; stepIndex?: number; kind?: string; id?: string; count?: number }>;
    }>(`/v1/journeys/${id}/node-stats`);
    const nodes: JourneyNodeStat[] = (res.data.nodes ?? []).map((n) => ({
      stepIndex: n.stepIndex ?? n.step_index ?? 0,
      executed: n.executed ?? 0,
      waiting: n.waiting ?? 0,
    }));
    const branches: JourneyBranchStat[] = (res.data.branches ?? []).map((b) => ({
      stepIndex: b.stepIndex ?? b.step_index ?? 0,
      kind: (b.kind ?? "variant") as JourneyForkKind,
      ...(b.id !== undefined ? { id: b.id } : {}),
      count: b.count ?? 0,
    }));
    return { nodes, branches };
  },
  /** Per-journey deliverability (delivered/opened/clicked rates over the journey's sends). */
  async delivery(id: string, days = 90): Promise<DeliverabilityResult> {
    const res = await apiClient.get<DeliverabilityResult>(`/v1/journeys/${id}/delivery`, { params: { days } });
    return res.data;
  },
  async templatesLibrary(): Promise<LibraryTemplate[]> {
    const res = await apiClient.get<{ templates: LibraryTemplate[] }>("/v1/journeys/templates-library");
    return res.data.templates;
  },
  async fromTemplate(input: FromTemplateInput): Promise<Journey> {
    const res = await apiClient.post<{ journey: Journey }>("/v1/journeys/from-template", input);
    return res.data.journey;
  },
  /**
   * «Сценарий возврата в один клик»: создаёт изолированный win-back-сценарий
   * под сегмент + триггер, кормящий его событием winback:<segmentId>.
   * Идемпотентно — повторный вызов вернёт тот же сценарий (created=false).
   */
  async winbackForSegment(segmentId: string): Promise<{ created: boolean; journey: Journey; trigger: WinbackTrigger | null }> {
    const res = await apiClient.post<{ created: boolean; journey: Journey; trigger: WinbackTrigger | null }>(
      "/v1/journeys/winback-for-segment", { segment_id: segmentId },
    );
    return res.data;
  },
  async seedLibrary(onlyKeys?: string[]): Promise<{ created: string[]; skipped: Array<{ key: string; reason: string }> }> {
    const res = await apiClient.post<{ created: string[]; skipped: Array<{ key: string; reason: string }> }>(
      "/v1/journeys/seed-library", { only_keys: onlyKeys },
    );
    return res.data;
  },
};

// ────────── helpers ──────────

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds} сек`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} мин`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1).replace(".0","")} ч`;
  return `${(seconds / 86400).toFixed(1).replace(".0","")} дн`;
}

export function statusBadgeClass(s: JourneyStatus): string {
  switch (s) {
    case "active":   return "badge-success";
    case "paused":   return "badge-warning";
    case "draft":    return "badge-neutral";
    case "archived": return "badge-neutral";
  }
}

export function runStateBadgeClass(s: JourneyRunState): string {
  switch (s) {
    case "enrolled":
    case "waiting":   return "badge-accent";
    case "running":   return "badge-accent";
    case "completed": return "badge-success";
    case "goal_hit":  return "badge-success";
    case "cancelled": return "badge-warning";
    case "failed":    return "badge-danger";
    case "holdout":   return "badge-neutral";
  }
}
